import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomPromptCheckSettings, GuardrailSettingsState, ProviderModel } from '../../../../../../gamma-api';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbSwitch from '../../../../../components/common/IbSwitch';
import IbTypography from '../../../../../components/common/IbTypography';
import { CodeEditorStatus } from '../../../../../components/common/CodeEditor/types';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { ITEM_CLASS_NAME, SECTION_CLASS_NAME } from '../../../../../components/common/TypedStepModal/const';
import ModelSelector from '../../../../../components/ModelSelector';
import { UseValidationForm } from '../../../../../utils/validationUtil';
import { ICheckStep } from '../../shared/utils';
import { CheckValidationField, CheckValidationForm } from '../CheckModal.validation';

import CommonCheckParameters from './CommonCheckParameters';

export interface ICustomPromptCheckParametersProps {
  step: ICheckStep;
  providers: ProviderModel[];
  providersLoading: boolean;
  validationFieldRefs: Partial<Record<CheckValidationField, React.RefObject<HTMLDivElement>>>;
  validationForm: UseValidationForm<CheckValidationForm>;
  onChange: (patch: Partial<CustomPromptCheckSettings>) => void;
}

const CustomPromptCheckParameters: React.FC<ICustomPromptCheckParametersProps> = ({
  step,
  providers,
  providersLoading,
  validationFieldRefs,
  validationForm,
  onChange,
}) => {
  const { t } = useTranslation();
  const check = step.check as CustomPromptCheckSettings;
  const { validationState, onFieldChange, onFieldBlur, getShowError, getErrorMessage } = validationForm;

  const onStateChange = (state: GuardrailSettingsState) => {
    onChange({ state });
    onFieldChange('state', state);
  };

  const onLlmChange = (llm: CustomPromptCheckSettings['llm']) => {
    onChange({ llm });
    onFieldChange('llm', llm);
  };

  const onPromptChange = (value: string) => {
    onChange({ prompt: value });
    onFieldChange('prompt', value);
  };

  const onCommonChange = (patch: Partial<CustomPromptCheckSettings>) => {
    onChange(patch);
  };

  const onLlmBlur = () => {
    onFieldBlur('llm');
  };

  const onUseFinalAnswerToolChange = (checked: boolean) => {
    onChange({ use_final_answer_tool: checked });
  };

  const onPromptBlur = () => {
    onFieldBlur('prompt');
  };

  const onConfidenceThresholdChange = (value: string | number) => {
    onChange({ confidence_threshold: value as number });
  };

  const onMaxTurnsChange = (value: string | number) => {
    onChange({ max_turns: value as number });
  };

  const onIncludeReasoningChange = (checked: boolean) => {
    onChange({ include_reasoning: checked });
  };

  return (
    <div className={SECTION_CLASS_NAME}>
      <CommonCheckParameters check={step.check} onChange={onCommonChange} onStateChange={onStateChange} />
      <div ref={validationFieldRefs.llm} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: Model: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: Model: Description')}
        </IbTypography.Paragraph>
        <ModelSelector.LLM
          descriptor={
            getShowError(validationState.llm) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.llm, t('Agent: Guardrails: CustomPromptCheck: Model: Error'))}
              </IbTypography.Paragraph>
            )
          }
          loading={providersLoading}
          providers={providers}
          settings={check.llm}
          status={getShowError(validationState.llm) ? 'error' : 'default'}
          onBlur={onLlmBlur}
          onChange={onLlmChange}
        />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={check.use_final_answer_tool ?? false} size="small" onChange={onUseFinalAnswerToolChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: CustomPromptCheck: UseFinalAnswerTool: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: UseFinalAnswerTool: Description')}
        </IbTypography.Paragraph>
      </div>
      <div ref={validationFieldRefs.prompt} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: Prompt: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: Prompt: Description')}
        </IbTypography.Paragraph>
        <PromptEditorExpandable
          promptLanguage={PromptLanguage.RESPONSE_GENERATION}
          status={getShowError(validationState.prompt) ? CodeEditorStatus.Error : CodeEditorStatus.Default}
          value={check.prompt}
          onBlur={onPromptBlur}
          onChange={onPromptChange}
        />
        {getShowError(validationState.prompt) && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.prompt, t('Agent: Guardrails: CustomPromptCheck: Prompt: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: ConfidenceThreshold: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: ConfidenceThreshold: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput
          max={1}
          min={0}
          step={0.01}
          value={check.confidence_threshold}
          onChange={onConfidenceThresholdChange}
        />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: MaxTurns: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: MaxTurns: Description')}
        </IbTypography.Paragraph>
        <IbNumberInput min={1} value={check.max_turns} onChange={onMaxTurnsChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={check.include_reasoning} size="small" onChange={onIncludeReasoningChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: CustomPromptCheck: IncludeReasoning: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: CustomPromptCheck: IncludeReasoning: Description')}
        </IbTypography.Paragraph>
      </div>
    </div>
  );
};

export default CustomPromptCheckParameters;
